/**
 * User Hooks
 * React Query hooks for user and person management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from 'config/queryClient';
import * as usersApi from 'services/api/users';

// =============================================================================
// User Queries
// =============================================================================

/**
 * Get current authenticated user
 */
export function useCurrentUser(options = {}) {
  return useQuery({
    queryKey: queryKeys.currentUser,
    queryFn: usersApi.getCurrentUser,
    staleTime: 10 * 60 * 1000, // 10 minutes - user data doesn't change often
    ...options
  });
}

/**
 * List users with optional filters
 * @param {Object} filters - Query filters
 */
export function useUsers(filters = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.users(filters),
    queryFn: () => usersApi.listUsers(filters),
    ...options
  });
}

/**
 * Get user by ID
 * @param {string} userId - User UUID
 */
export function useUser(userId, options = {}) {
  return useQuery({
    queryKey: queryKeys.user(userId),
    queryFn: () => usersApi.getUser(userId),
    enabled: !!userId,
    ...options
  });
}

/**
 * List persons with optional filters
 * @param {Object} filters - Query filters
 */
export function usePersons(filters = {}, options = {}) {
  return useQuery({
    queryKey: ['persons', filters],
    queryFn: () => usersApi.listPersons(filters),
    ...options
  });
}

/**
 * Get person by ID
 * @param {string} personId - Person UUID
 */
export function usePerson(personId, options = {}) {
  return useQuery({
    queryKey: ['persons', personId],
    queryFn: () => usersApi.getPerson(personId),
    enabled: !!personId,
    ...options
  });
}

/**
 * Get staff list
 * @param {Object} filters - Query filters
 */
export function useStaff(filters = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.staff(filters),
    queryFn: () => usersApi.getStaff(filters),
    ...options
  });
}

/**
 * Get guardians list
 * @param {Object} filters - Query filters
 */
export function useGuardians(filters = {}, options = {}) {
  return useQuery({
    queryKey: ['guardians', filters],
    queryFn: () => usersApi.getGuardians(filters),
    ...options
  });
}

/**
 * List all roles
 */
export function useRoles(options = {}) {
  return useQuery({
    queryKey: queryKeys.roles,
    queryFn: usersApi.listRoles,
    staleTime: 30 * 60 * 1000, // 30 minutes - roles rarely change
    ...options
  });
}

/**
 * Get person's roles
 * @param {string} personId - Person UUID
 */
export function usePersonRoles(personId, options = {}) {
  return useQuery({
    queryKey: ['persons', personId, 'roles'],
    queryFn: () => usersApi.getPersonRoles(personId),
    enabled: !!personId,
    ...options
  });
}

/**
 * Children in a supervisor's scope with allocation flags (org admin only).
 * @param {string} personId - Supervisor's Person UUID
 */
export function useSupervisableChildren(personId, options = {}) {
  return useQuery({
    queryKey: ['persons', personId, 'supervisable-children'],
    queryFn: () => usersApi.getSupervisableChildren(personId),
    enabled: !!personId,
    ...options
  });
}

// =============================================================================
// User Mutations
// =============================================================================

/**
 * Create user mutation
 */
export function useCreateUser(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: usersApi.createUser,
    onSuccess: () => {
      // Invalidate users list
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['persons'] });
    },
    ...options
  });
}

/**
 * Grant access mutation — Quick add and the guided Add user flow.
 *
 * Exists so the two callers stop invoking usersApi.grantAccess directly: a bare
 * API call writes a user the ['users'] query knows nothing about, so the list
 * kept serving its cached rows and a freshly added (or restored) account only
 * appeared after the cache went stale or the page was reloaded.
 */
export function useGrantAccess(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: usersApi.grantAccess,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      // A grant writes rbac_person and its role assignments too, so the person
      // and role-scoped lists are stale for exactly the same reason.
      queryClient.invalidateQueries({ queryKey: ['persons'] });
    },
    ...options
  });
}

/**
 * Update user mutation
 */
export function useUpdateUser(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }) => usersApi.updateUser(userId, data),
    onSuccess: (_, { userId }) => {
      // Invalidate specific user and list
      queryClient.invalidateQueries({ queryKey: queryKeys.user(userId) });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    ...options
  });
}

/**
 * Update current user profile mutation
 */
export function useUpdateCurrentUser(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: usersApi.updateCurrentUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
    ...options
  });
}

/**
 * Delete user mutation
 */
export function useDeleteUser(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: usersApi.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['persons'] });
    },
    ...options
  });
}

/**
 * Update person mutation
 */
export function useUpdatePerson(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ personId, data }) => usersApi.updatePerson(personId, data),
    onSuccess: (_, { personId }) => {
      queryClient.invalidateQueries({ queryKey: ['persons', personId] });
      queryClient.invalidateQueries({ queryKey: ['persons'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    ...options
  });
}

/**
 * Create person mutation
 */
export function useCreatePerson(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: usersApi.createPerson,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['persons'] });
    },
    ...options
  });
}

/**
 * Send a password-reset link to a user (admin action).
 * Accepts { userId } only — admins do not set other users' passwords.
 */
export function useResetUserPassword(options = {}) {
  return useMutation({
    mutationFn: ({ userId }) => usersApi.resetUserPassword(userId),
    ...options
  });
}

/**
 * Activate user mutation
 */
export function useActivateUser(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: usersApi.activateUser,
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user(userId) });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    ...options
  });
}

/**
 * Deactivate user mutation
 */
export function useDeactivateUser(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: usersApi.deactivateUser,
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user(userId) });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    ...options
  });
}

/**
 * Assign role to person mutation
 */
export function useAssignRole(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ personId, roleId, isPrimary }) => usersApi.assignRole(personId, roleId, isPrimary),
    onSuccess: (_, { personId }) => {
      queryClient.invalidateQueries({ queryKey: ['persons', personId, 'roles'] });
      queryClient.invalidateQueries({ queryKey: ['persons', personId] });
    },
    ...options
  });
}

/**
 * Remove role from person mutation
 */
export function useRemoveRole(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ personId, roleId }) => usersApi.removeRole(personId, roleId),
    onSuccess: (_, { personId }) => {
      queryClient.invalidateQueries({ queryKey: ['persons', personId, 'roles'] });
      queryClient.invalidateQueries({ queryKey: ['persons', personId] });
    },
    ...options
  });
}

/**
 * Batch allocate / deallocate children for a supervisor (org admin only).
 */
export function useUpdateSupervisorAllocations(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ personId, allocate, deallocate }) => usersApi.updateSupervisorAllocations(personId, { allocate, deallocate }),
    onSuccess: (_, { personId }) => {
      queryClient.invalidateQueries({ queryKey: ['persons', personId, 'supervisable-children'] });
      // Allocations widen a supervisor's client visibility, so the clients list
      // filtered by supervisor is stale.
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    ...options
  });
}

// =============================================================================
// Self-service Profile & Contact Verification
// =============================================================================

/**
 * The signed-in user's own profile (addresses, designation, verification state)
 */
export function useMyProfile(options = {}) {
  return useQuery({
    queryKey: ['myProfile'],
    queryFn: usersApi.getMyProfile,
    staleTime: 5 * 60 * 1000,
    ...options
  });
}

/**
 * Update the signed-in user's own profile
 */
export function useUpdateMyProfile(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: usersApi.updateMyProfile,
    onSuccess: (data) => {
      // Seed the cache from the response so the form stops showing stale values
      // before the refetch lands.
      queryClient.setQueryData(['myProfile'], data);
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
    ...options
  });
}

/**
 * Upload (replacing any existing) the signed-in user's profile picture.
 * Pass a FormData with `display` and `thumbnail` JPEG blobs.
 */
export function useUploadMyAvatar(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: usersApi.uploadMyAvatar,
    onSuccess: () => {
      // The response carries the new URL, but the picture lives in several
      // profile fields; refetch rather than hand-patch each cache entry.
      queryClient.invalidateQueries({ queryKey: ['myProfile'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
    ...options
  });
}

/**
 * Remove the signed-in user's profile picture.
 */
export function useDeleteMyAvatar(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: usersApi.deleteMyAvatar,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myProfile'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
    ...options
  });
}

/**
 * Verification state for the signed-in user (gates MFA enrolment)
 */
export function useMyContactStatus(options = {}) {
  return useQuery({
    queryKey: ['myContactStatus'],
    queryFn: usersApi.getMyContactStatus,
    ...options
  });
}

/**
 * Start an email change (sends a confirmation link to the new address)
 */
export function useRequestEmailChange(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ newEmail, currentPassword }) => usersApi.requestEmailChange(newEmail, currentPassword),
    onSuccess: () => {
      // pending_email changed; the profile shows it as "awaiting confirmation".
      queryClient.invalidateQueries({ queryKey: ['myProfile'] });
      queryClient.invalidateQueries({ queryKey: ['myContactStatus'] });
    },
    ...options
  });
}

/**
 * Change the signed-in user's own password
 */
export function useChangeMyPassword(options = {}) {
  return useMutation({
    mutationFn: ({ currentPassword, newPassword }) => usersApi.changeMyPassword(currentPassword, newPassword),
    ...options
  });
}

/**
 * Send a verification OTP to a new/changed phone number
 */
export function useRequestPhoneVerification(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ phone }) => usersApi.requestPhoneVerification(phone),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myProfile'] });
      queryClient.invalidateQueries({ queryKey: ['myContactStatus'] });
    },
    ...options
  });
}

/**
 * Confirm a phone number with its OTP
 */
export function useVerifyPhone(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ code }) => usersApi.verifyPhone(code),
    onSuccess: (data) => {
      queryClient.setQueryData(['myContactStatus'], data);
      queryClient.invalidateQueries({ queryKey: ['myProfile'] });
    },
    ...options
  });
}

// =============================================================================
// Invites
// =============================================================================

/**
 * Re-issue and re-send a user's pending invite (admin action)
 */
export function useResendInvite(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId }) => usersApi.resendInvite(userId),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user(userId) });
    },
    ...options
  });
}
